import {
    Box,
    VStack,
    Icon,
    Text,
    Flex,
    Divider,
    useColorModeValue,
    Image,
    BoxProps,
    CloseButton,
} from '@chakra-ui/react';

import { NavLink, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { BanditIcon } from '../Common/BanditIcon';
import {
    MdDashboard,
    MdSearch,
    MdAttachMoney,
    MdSettings,
    MdInbox,
    MdTrackChanges,
    MdBusiness,

} from 'react-icons/md';
import { IconType } from 'react-icons';

interface NavItemProps {
    icon: IconType;
    label: string;
    to: string;
    isActive?: boolean;
}

function NavItem({ icon, label, to, isActive }: NavItemProps) {
    const activeBg = 'brand.500';
    const activeColor = 'white';
    const inactiveColor = useColorModeValue('black', 'gray.300');
    const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.200');

    return (
        <NavLink to={to} style={{ width: '100%', textDecoration: 'none' }}>
            <Flex
                align="center"
                px={4}
                py={3}
                borderRadius="lg"
                cursor="pointer"
                transition="all 0.2s ease-in-out"
                bg={isActive ? activeBg : 'transparent'}
                color={isActive ? activeColor : inactiveColor}
                fontWeight={isActive ? 'bold' : 'medium'}
                _hover={{
                    bg: isActive ? 'brand.600' : hoverBg,
                    color: isActive ? 'white !important' : 'inherit',
                    transform: isActive ? 'none' : 'translateX(4px)',
                }}
                sx={{
                    '& *': {
                        color: isActive ? 'white !important' : 'inherit',
                    }
                }}
            >
                <Icon as={icon} boxSize={5} mr={3} />
                <Text fontSize="sm">
                    {label}
                </Text>
            </Flex>
        </NavLink>
    );
}

const navItems = [
    { icon: MdDashboard, label: 'Dashboard', to: '/dashboard' },
    { icon: MdSearch, label: 'Search', to: '/search' },
    { icon: MdInbox, label: 'Imported Bids', to: '/imported' },
    { icon: MdTrackChanges, label: 'My Pursuits', to: '/pursuits' },
    // { icon: MdFolder, label: 'Projects', to: '/projects' },
    { icon: MdAttachMoney, label: 'Price Sets', to: '/price-sets' },
];


const configItems = [
    { icon: MdBusiness, label: 'Agencies', to: '/agencies' },
    // { icon: MdAdminPanelSettings, label: 'Admin', to: '/admin' },
];


interface SidebarProps extends BoxProps {
    onClose?: () => void;
}

export function SidebarContent({ onClose, ...rest }: SidebarProps) {
    const location = useLocation();
    const { user } = useUser();

    const bgVal = useColorModeValue('white', 'navy.800');
    const borderVal = useColorModeValue('gray.200', 'navy.600');
    const titleColor = useColorModeValue('gray.800', 'white');
    const subtitleColor = useColorModeValue('gray.500', 'gray.400');

    // Get logo from public metadata if available
    const customLogoUrl = (user?.publicMetadata?.logoUrl as string) || null;

    return (
        <Box
            bg={bgVal}
            borderRight="1px solid"
            borderColor={borderVal}
            w="280px"
            pos="fixed"
            h="full"
            {...rest}
        >
            <Flex h="20" alignItems="center" px="4" justifyContent="space-between" mx="4">
                <Flex align="center">
                    {customLogoUrl ? (
                        <Image
                            src={customLogoUrl}
                            alt="Organization Logo"
                            boxSize="40px"
                            objectFit="contain"
                            mr={3}
                            borderRadius="md"
                        />
                    ) : (
                        <BanditIcon boxSize={8} mr={3} variant="default" />
                    )}
                    <Box>
                        <Text fontWeight="bold" fontSize="sm" color={titleColor}>BidBandit</Text>
                        <Text fontSize="10px" color={subtitleColor}>Procurement Intel</Text>
                    </Box>
                </Flex>
                <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
            </Flex>

            <VStack spacing={1} px={4} align="stretch" mt={4}>
                {navItems.map((item) => (
                    <NavItem
                        key={item.to}
                        icon={item.icon}
                        label={item.label}
                        to={item.to}
                        isActive={location.pathname === item.to}
                    />
                ))}
            </VStack>

            <Divider my={6} borderColor={borderVal} />

            <VStack spacing={1} px={4} align="stretch">
                <Text fontSize="xs" color={subtitleColor} px={4} mb={2} textTransform="uppercase" fontWeight="bold">
                    Administration
                </Text>
                {configItems.map((item) => (
                    <NavItem
                        key={item.to}
                        icon={item.icon}
                        label={item.label}
                        to={item.to}
                        isActive={location.pathname === item.to}
                    />
                ))}
                <NavItem
                    icon={MdSettings}
                    label="Settings"
                    to="/settings"
                    isActive={location.pathname === '/settings'}
                />
            </VStack>
        </Box>
    );
}

export function Sidebar() {
    return (
        <SidebarContent
            display={{ base: 'none', md: 'block' }}
        />
    );
}
